import * as lookups from 'modules/api/lookups';
import { cancelFunctions } from 'utils/cancelFunctions';
import { useSelector } from 'react-redux';
import { getAdvertiser } from 'store/dealManagement/selectors/getCommonDealDetails';
import { CodeNameModel } from 'components/common/types';
import { PersonType } from 'components/common/types/SalesPerson.types';
import { getProductCategories } from 'modules/api/content';
import { AsyncSelectLoadDataHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { BrandWithProductCategory } from 'components/common/types/Deal.types';

export const useLoadLookup = (
  minQueryLength: number = 1,
): {
  onLoadLookup: typeof onLoadLookup;
  onAssetLookup: typeof onAssetLookup;
  onLoadBrands: typeof onLoadBrands;
  onLoadSalesPerson: typeof onLoadSalesPerson;
  onLoadSalesTeam: typeof onLoadSalesTeam;
  onLoadAdminPerson: typeof onLoadAdminPerson;
  onLoadProductCategory: typeof onLoadProductCategory;
} => {
  const advertiser = useSelector(getAdvertiser);

  const onLoadLookup: AsyncSelectLoadDataHandler<CodeNameModel> = async (lookupName, query) => {
    if (query.length <= minQueryLength) return [];
    try {
      return await lookups.getLookupData({ cancelFunctions, lookupURLName: lookupName, query });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch ${lookupName}:`, error);
      return [];
    }
  };

  const onAssetLookup: AsyncSelectLoadDataHandler<CodeNameModel> = async (lookupName, query) => {
    if (query.length <= minQueryLength) return [];
    try {
      return await lookups.getAssetLookup({ cancelFunctions, lookupURLName: lookupName, query });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch ${lookupName}:`, error);
      return [];
    }
  };

  const onLoadBrands: AsyncSelectLoadDataHandler<BrandWithProductCategory> = async (_, query) => {
    if (query.length <= minQueryLength) return [];

    try {
      return await lookups.getBrandLookupData(cancelFunctions, query, advertiser?.code);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch brands:', error);
      return [];
    }
  };

  const onLoadSalesPerson: AsyncSelectLoadDataHandler<CodeNameModel> = async (_, query) => {
    if (query.length <= minQueryLength) return [];

    try {
      const data = await lookups.getSalesPersonLookup(cancelFunctions, query);
      return data.filter((value: CodeNameModel) => value.category === PersonType.SALES) || [];
    } catch {
      return [];
    }
  };

  const onLoadAdminPerson: AsyncSelectLoadDataHandler<CodeNameModel> = async (_, query) => {
    if (query.length <= minQueryLength) return [];

    try {
      const data = await lookups.getSalesPersonLookup(cancelFunctions, query);
      return data.filter((value: CodeNameModel) => value.category === PersonType.ADMIN) || [];
    } catch {
      return [];
    }
  };

  const onLoadSalesTeam: AsyncSelectLoadDataHandler<CodeNameModel> = async (_, query) => {
    if (query.length <= minQueryLength) return [];

    try {
      return await lookups.getSalesTeamLookup(cancelFunctions, query);
    } catch {
      return [];
    }
  };

  const sortByRelevance = (results: CodeNameModel[], query: string): CodeNameModel[] => {
    const lowerQuery = query.toLowerCase();

    return [...results].sort((a, b) => {
      const aStartsWith = a.name.toLowerCase().startsWith(lowerQuery);
      const bStartsWith = b.name.toLowerCase().startsWith(lowerQuery);

      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;

      return a.name.localeCompare(b.name);
    });
  };

  const onLoadProductCategory: AsyncSelectLoadDataHandler<CodeNameModel> = async (_, query) => {
    if (query.length <= minQueryLength) return [];

    try {
      const results = await getProductCategories(cancelFunctions, query);
      return sortByRelevance(results, query);
    } catch {
      return [];
    }
  };

  return {
    onLoadLookup,
    onAssetLookup,
    onLoadBrands,
    onLoadSalesPerson,
    onLoadSalesTeam,
    onLoadAdminPerson,
    onLoadProductCategory,
  };
};
